import { fetchAuthSession } from 'aws-amplify/auth';
import { apiConfig } from '../config/env';
import { APIError } from '@poker-tracker/shared';

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status: number;
  data: APIError | null;
  isNetworkError: boolean;
  isOfflineError: boolean;
  retryable: boolean;
  
  constructor(message: string, status: number, data: APIError | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.isNetworkError = status === 0;
    this.isOfflineError = message.includes('offline') || message.includes('network') || status === 0;
    
    // Determine if error is retryable
    this.retryable = (
      // Network errors are retryable
      this.isNetworkError ||
      // Server errors (5xx) are retryable
      (status >= 500 && status < 600) ||
      // Too many requests (429) is retryable after a delay
      status === 429
    );
  }
}

/**
 * Check if the browser is online
 */
export const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? navigator.onLine
    : true;
};

/**
 * Base API client for making authenticated requests to the backend
 */
export const apiClient = {
  /**
   * Get the current authentication token from Amplify
   */
  async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  },

  /**
   * Make a request to the API with authentication and retry logic
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryConfig = { maxRetries: 3, baseDelay: 1000, maxDelay: 10000 }
  ): Promise<T> {
    // Check if we're offline before making the request
    if (!isOnline()) {
      throw new ApiError('You appear to be offline. Please check your internet connection and try again.', 0);
    }
    
    const executeRequest = async (retryCount = 0): Promise<T> => {
      try {
        const token = await this.getAuthToken();
        const url = `${apiConfig.baseURL}${endpoint}`;
        
        console.log(`Making API request to: ${url}`);
        
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...options.headers,
        };

        // Add auth token if available
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Handle data option for request body
        const config: RequestInit = {
          ...options,
          headers,
        };

        // Convert data option to body for requests that support it
        if ('data' in options && options.data) {
          config.body = JSON.stringify(options.data);
          // Remove the data property as it's not a valid RequestInit property
          delete (config as any).data;
        }

        const response = await fetch(url, config);
        
        console.log(`API Response for ${endpoint}:`, {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          ok: response.ok
        });
        
        // Handle non-JSON responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log(`API Response data for ${endpoint}:`, data);
          
          // Handle API error responses
          if (!response.ok) {
            const apiError = new ApiError(
              data.error?.message || 'An error occurred',
              response.status,
              data as APIError
            );
            
            // Check if we should retry
            if (apiError.retryable && retryCount < retryConfig.maxRetries) {
              // Calculate exponential backoff delay with jitter
              const delay = Math.min(
                retryConfig.baseDelay * Math.pow(2, retryCount) + Math.random() * 1000,
                retryConfig.maxDelay
              );
              
              console.log(`Retrying request (${retryCount + 1}/${retryConfig.maxRetries}) after ${delay}ms`);
              
              // Wait for the calculated delay
              await new Promise(resolve => setTimeout(resolve, delay));
              
              // Retry the request
              return executeRequest(retryCount + 1);
            }
            
            throw apiError;
          }
          
          return data as T;
        } else {
          // Handle non-JSON responses (like binary data)
          if (!response.ok) {
            const errorText = await response.text();
            throw new ApiError(
              errorText || 'An error occurred',
              response.status
            );
          }
          
          return await response.text() as unknown as T;
        }
      } catch (error) {
        if (error instanceof ApiError) {
          // If it's already an ApiError and retryable, retry
          if (error.retryable && retryCount < retryConfig.maxRetries) {
            const delay = Math.min(
              retryConfig.baseDelay * Math.pow(2, retryCount) + Math.random() * 1000,
              retryConfig.maxDelay
            );
            
            console.log(`Retrying request (${retryCount + 1}/${retryConfig.maxRetries}) after ${delay}ms`);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            return executeRequest(retryCount + 1);
          }
          
          throw error;
        }
        
        // Handle network errors
        if (error instanceof Error) {
          const apiError = new ApiError(error.message, 0);
          
          // Check if we should retry network errors
          if (apiError.retryable && retryCount < retryConfig.maxRetries) {
            const delay = Math.min(
              retryConfig.baseDelay * Math.pow(2, retryCount) + Math.random() * 1000,
              retryConfig.maxDelay
            );
            
            console.log(`Retrying request after network error (${retryCount + 1}/${retryConfig.maxRetries}) after ${delay}ms`);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            return executeRequest(retryCount + 1);
          }
          
          throw apiError;
        } else {
          throw new ApiError('Network error', 0);
        }
      }
    };
    
    return executeRequest();
  },

  /**
   * GET request helper
   */
  async get<T>(endpoint: string, options: RequestInit & { params?: Record<string, string> } = {}): Promise<T> {
    let url = endpoint;
    
    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
      
      // Remove params from options since it's not a valid RequestInit property
      const { params, ...requestOptions } = options;
      return this.request<T>(url, {
        method: 'GET',
        ...requestOptions,
      });
    }
    
    return this.request<T>(url, {
      method: 'GET',
      ...options,
    });
  },

  /**
   * POST request helper
   */
  async post<T>(
    endpoint: string,
    data: any,
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  },

  /**
   * PUT request helper
   */
  async put<T>(
    endpoint: string,
    data: any,
    options: RequestInit = {}
  ): Promise<T> {
    console.log('ApiClient: PUT request to', endpoint, 'with data:', data);
    console.log('ApiClient: Stringified data:', JSON.stringify(data));
    
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  },

  /**
   * PATCH request helper
   */
  async patch<T>(
    endpoint: string,
    data: any,
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  },

  /**
   * DELETE request helper
   */
  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  },
};